import { FH_COMPONENT, FH_FIELD_TYPE, FH_FLAG, FH_PAYLOAD, FH_SECTION_KIND } from './company-form.constants';
import {
  CompanyFieldDescriptor,
  CompanyFormSection,
  FhFieldState,
  FhRebateRow,
  FhUploadedFile,
} from './company-form.types';

// Assembles the request body from the form, keyed by each field's `payload` (which is NOT the
// field id — e.g. billingmail -> invoicing_email). Payload keys are verified against docs/swagger.json in
// makers-finances-mgr (github.com/rappi-inc/makers-finances-mgr, dto/company.CreateRequest).
//
// Hidden sections and hidden fields are skipped entirely: a field the rules engine turned off is
// not part of this company's shape and must not reach the backend.
//
// Two shapes the descriptors cannot express on their own:
//  - FILE fields do not become their own body key. The backend takes a single top-level
//    `documents` array (field_name/document_key/document_name), so every visible FILE field's
//    uploads are collected into that one array instead. This only applies to a company that does
//    not exist yet (see `includeDocuments` below) — per
//    docs/documents/COMPANY_DOCUMENTS_ARCHITECTURE.md §10 ("Key Differences: Create vs Add
//    Documents"), only POST /companies takes documents inline; an existing company's documents are
//    added/removed one at a time via POST/DELETE /companies/:id/documents, which
//    CompanyFilesCardComponent calls directly as soon as the company has an id.
//  - Agency Rebates are NOT part of this body at all — POST /companies has no `agency_rebates`
//    field. Each row is created afterwards via POST /companies/{id}/agency-rebates (see
//    MakersFinancesMgrService.createAgencyRebate), which requires a company id that does not
//    exist yet while this payload is being built.

export interface BuildPayloadArgs {
  sections: CompanyFormSection[];
  /** controlName -> runtime state from the rules engine. */
  state: Map<string, FhFieldState>;
  /** controlName -> current value (use form.getRawValue() so disabled controls are included). */
  values: Record<string, unknown>;
  /** Session country; always sent, since the country field is UI-inferred and read-only. */
  country: string;
  /**
   * Whether FILE field uploads should be collected into the top-level `documents` array. Defaults
   * to true (a brand-new company: POST /companies is the only way to attach its first documents).
   * Pass false once the company has an id — its documents are already persisted individually by
   * CompanyFilesCardComponent, and re-sending them on PUT /companies/:id would attempt to re-add
   * documents that already exist (409 Conflict).
   */
  includeDocuments?: boolean;
}

export interface DocumentPayload {
  field_name: string;
  document_key: string;
  document_name: string;
}

export function buildCompanyPayload(args: BuildPayloadArgs): Record<string, unknown> {
  const { sections, state, values, country, includeDocuments = true } = args;
  const body: Record<string, unknown> = {};
  const documents: DocumentPayload[] = [];

  sections.forEach(section => {
    if (section.kind === FH_SECTION_KIND.REBATES) {
      // Agency Rebates never join this body — see the module comment above.
      return;
    }
    // Checked independently of each field: the caller may hand us per-field state that has not
    // folded section visibility in, and a hidden section must never contribute to the body.
    if (!state.get(sectionStateKey(section.key))?.visible) {
      return;
    }
    section.fields.forEach(field => {
      const fieldState = state.get(field.controlName);
      if (!fieldState || !fieldState.visible) {
        return;
      }
      if (field.type === FH_FIELD_TYPE.FILE) {
        if (includeDocuments) {
          documents.push(...serializeDocuments(field, values[field.controlName]));
        }
        return;
      }
      const serialized = serializeField(field, values[field.controlName]);
      if (serialized === undefined) {
        return;
      }
      if (field.valuePayload && field.type === FH_FIELD_TYPE.SELECT && !field.multiple) {
        // Send code to the main payload field and label to the valuePayload field
        const code = serialized as string;
        const option = field.options.find(opt => opt.id === code);
        body[field.payload] = code;
        body[field.valuePayload] = option?.name ?? null;
      } else {
        body[field.payload] = serialized;
      }
    });
  });

  // The country field is inferred from the session and rendered read-only, so it bypasses the
  // visibility gate and is always sent. The DTO also requires it as a non-empty top-level field
  // even though the backend overrides it with the `country` header on arrival.
  body[FH_PAYLOAD.COUNTRY] = country;

  if (documents.length) {
    body['documents'] = documents;
  }

  return body;
}

// The rules engine records section visibility under a synthetic key so payload assembly and the
// template agree on one source of truth.
export function rebatesSectionStateKey(section: CompanyFormSection): string {
  return sectionStateKey(section.key);
}

export function sectionStateKey(sectionKey: string): string {
  return `section:${sectionKey}`;
}

// Returns `undefined` for anything that should be omitted from the body. `false` and `0` are
// meaningful values and are kept. FILE fields never reach here — buildCompanyPayload routes them
// to serializeDocuments instead, since they don't become their own body key.
export function serializeField(field: CompanyFieldDescriptor, raw: unknown): unknown {
  if (isOmitted(raw)) {
    return undefined;
  }

  // Phone numbers are always strings regardless of what the backend declares as the field type.
  if (field.component === FH_COMPONENT.PHONE) {
    return String(raw);
  }

  // `overwrite_payload_type` (companies-form.config.json metadata) lets a field be sent as a
  // different type than the one it renders as — e.g. financial_cut_off_day renders as whatever
  // control the backend's field type calls for, but must reach the API as a number.
  switch (field.overwritePayloadType ?? field.type) {
    case FH_FIELD_TYPE.NUMBER: {
      const parsed = Number(raw);
      return isNaN(parsed) ? undefined : parsed;
    }
    // Also true when a BOOLEAN field rendered as a select hands back the stringified id
    // ("true"/"false") instead of the native boolean it started with — see FH_BOOLEAN_OPTIONS.
    case FH_FIELD_TYPE.BOOLEAN:
      return raw === true || raw === FH_FLAG.TRUE;
    case FH_FIELD_TYPE.SELECT:
      // FhSelectComponent guarantees the control holds an id (or an array of ids).
      return field.multiple
        ? (raw as unknown[]).map(value => String(value))
        : String(raw);
    default:
      return typeof raw === 'string' ? raw.trim() : raw;
  }
}

// Maps a FILE field's uploaded files onto the shape POST/PUT /companies expects inside its single
// top-level `documents` array. `field.payload` (e.g. "tax_document", "contract") becomes
// `field_name`, letting the backend tell which card each document came from.
export function serializeDocuments(field: CompanyFieldDescriptor, raw: unknown): DocumentPayload[] {
  if (isOmitted(raw)) {
    return [];
  }
  return (raw as FhUploadedFile[]).map(file => ({
    field_name: field.payload,
    document_key: file.key,
    document_name: file.name,
  }));
}

function isOmitted(value: unknown): boolean {
  if (value === null || value === undefined || value === '') {
    return true;
  }
  return Array.isArray(value) && value.length === 0;
}

// Rows are kept sorted in the UI on blur; this re-sorts defensively so the payload is ordered
// regardless of UI state. Rows without a threshold sort last.
export function sortRebates(rows: FhRebateRow[]): FhRebateRow[] {
  return [...rows].sort((a, b) => {
    const left = a?.rebate_threshold;
    const right = b?.rebate_threshold;
    if (left === null || left === undefined) {
      return 1;
    }
    if (right === null || right === undefined) {
      return -1;
    }
    return Number(left) - Number(right);
  });
}
